export interface SetupItem {
  id: string
  name: string
  value: string | null
  category: string
  link: string | null
  position: number
}

export const SETUP_CATEGORIES = [
  { id: 'pc', label: 'PC / Specs', icon: 'i-lucide-cpu' },
  { id: 'monitor', label: 'Monitor', icon: 'i-lucide-monitor' },
  { id: 'keyboard', label: 'Keyboard', icon: 'i-lucide-keyboard' },
  { id: 'mouse', label: 'Mouse', icon: 'i-lucide-mouse' },
  { id: 'headset', label: 'Headset', icon: 'i-lucide-headphones' },
  { id: 'microphone', label: 'Microphone', icon: 'i-lucide-mic' },
  { id: 'controller', label: 'Controller', icon: 'i-lucide-gamepad-2' },
  { id: 'chair', label: 'Chair', icon: 'i-lucide-armchair' },
  { id: 'other', label: 'Other', icon: 'i-lucide-box' }
] as const

export function getCategoryLabel(id: string): string {
  return SETUP_CATEGORIES.find(c => c.id === id)?.label ?? id
}

export function getCategoryIcon(id: string): string {
  return SETUP_CATEGORIES.find(c => c.id === id)?.icon ?? 'i-lucide-box'
}

export const useSetupsStore = defineStore('setups', () => {
  const items = ref<SetupItem[]>([])
  const photoUrl = ref<string | null>(null)
  const fetchedAt = ref<number | null>(null)
  const isLoading = ref(false)

  const MAX_AGE_MS = 23 * 60 * 60 * 1000

  function isStale() {
    if (!fetchedAt.value) return true
    return Date.now() - fetchedAt.value > MAX_AGE_MS
  }

  async function fetchSetups(characterId: string, force = false) {
    if (!force && !isStale() && (items.value.length > 0 || photoUrl.value)) {
      return { items: items.value, photoUrl: photoUrl.value }
    }

    isLoading.value = true
    try {
      const data = await $fetch<{ items: SetupItem[], photo_url: string | null }>(`/api/character/${characterId}/setup`)
      items.value = data.items
      photoUrl.value = data.photo_url
      fetchedAt.value = Date.now()
      return { items: data.items, photoUrl: data.photo_url }
    } catch (error) {
      console.error('Failed to fetch setup items:', error)
      items.value = []
      photoUrl.value = null
      return { items: [], photoUrl: null }
    } finally {
      isLoading.value = false
    }
  }

  function invalidate() {
    fetchedAt.value = null
  }

  function clear() {
    items.value = []
    photoUrl.value = null
    fetchedAt.value = null
  }

  return {
    items,
    photoUrl,
    isLoading,
    fetchedAt,
    fetchSetups,
    invalidate,
    clear
  }
})
